/**
 * Collection of useful components relating to Search Results:
 *
 * - TotalCount
 * - SearchTerm
 * - ArticleTitle
 * - ArticleLink
 * - CategoryTitle
 * - CategoryLink
 * - Snippet
 * - LoopResults
 * - IsEmpty
 * - IsNotEmpty
 * - ShowMoreButton
 *
 * @module
 */
import React, { useContext } from 'react';
import { flush } from '@elevio/js-analytics';
import { useSelector, useDispatch } from '../actions/types';
import { Actions as SearchActions } from '../actions/search';
import { SearchResultContext } from '../contexts';
import { useSearchResults } from '../hooks';
import { Context as ConfigContext } from '../Config';
import * as Meta from './meta';
import * as Article from './article';
import * as Category from './category';
/**
 * Print out the total number of search results available.
 *
 * **💡 NOTE**: this will print `null` if no search results are available yet.
 */
export const TotalCount = () => {
    const { totalCount } = useSearchResults();
    return React.createElement(React.Fragment, null, totalCount);
};
TotalCount.displayName = 'SearchResults.TotalCount';
/**
 * Print out the search term that the current results are showing for.
 *
 * > **💡 NOTE**: will print out `null` if there isn't a current search.
 */
export const SearchTerm = () => {
    const { searchTerm } = useSearchResults();
    return React.createElement(React.Fragment, null, searchTerm);
};
SearchTerm.displayName = 'SearchResults.SearchTerm';
export const ArticleTitle = ({ className }) => {
    const searchResult = React.useContext(SearchResultContext);
    if (!searchResult)
        return null;
    return React.createElement("span", { className: className }, searchResult.title);
};
ArticleTitle.displayName = 'SearchResults.ArticleTitle';
/**
 * Renders an `<a/>` tag with a link to the search results article.
 */
export const ArticleLink = (props) => {
    const config = useContext(ConfigContext);
    const dispatch = useDispatch();
    const searchResult = React.useContext(SearchResultContext);
    const onClick = async (e) => {
        searchResult &&
            dispatch(SearchActions.searchResultClick(searchResult.id, searchResult.title, searchResult.index));
        // Doing this to make sure the event has a chance to be sent.
        if (config.forceRefreshRouting) {
            // After the await currentTarget is undefined
            const href = e.currentTarget.href;
            e.preventDefault();
            await flush();
            window.location.href = href;
        }
    };
    if (!searchResult)
        return null;
    return (React.createElement(Article.Link, { ...props, articleId: searchResult.id, slug: searchResult.slug, onClick: onClick }));
};
ArticleLink.displayName = 'SearchResults.ArticleLink';
/**
 * Print out the article intro from the current search result.
 */
// export const Intro = () => {
//   const searchResult = React.useContext(SearchResultContext);
//   if (!searchResult) return null;
//   return <>{truncate(searchResult.bodyText, {'length': 200})}</>;
// };
/**
 * Print out the article last updated time from the current search result.
 */
// export const ArticleUpdatedAt = () => {
//   const searchResult = React.useContext(SearchResultContext);
//   if (!searchResult) return null;
//   return <>{format(new Date(searchResult.lastUpdatedAt), 'MMM d, yyyy h:mma')}</>;
// };
/**
 * Print out the title of the category of the current search result.
 */
export const CategoryTitle = () => {
    const searchResult = React.useContext(SearchResultContext);
    if (!searchResult)
        return null;
    return React.createElement(React.Fragment, null, searchResult.category.title);
};
CategoryTitle.displayName = 'SearchResults.CategoryTitle';
/**
 * Link to the category of the current search result.
 */
export const CategoryLink = (props) => {
    const searchResult = React.useContext(SearchResultContext);
    if (!searchResult)
        return null;
    return React.createElement(Category.Link, { ...props, categoryId: searchResult.category.id });
};
CategoryLink.displayName = 'SearchResults.CategoryLink';
/**
 * This renders all the body snippets that match the current search for the current result.
 *
 * @param props any props that you can pass to a div
 */
export const Snippet = (props) => {
    const searchResult = React.useContext(SearchResultContext);
    if (!(searchResult &&
        searchResult.highlights &&
        searchResult.highlights.body.length > 0))
        return null;
    return React.createElement(Meta.HTML, { ...props }, searchResult.highlights.body[0]);
};
Snippet.displayName = 'SearchResults.Snippet';
const isSearchResultFunction = (arg) => {
    return typeof arg === 'function';
};
function returnSearchResultChildren(children, result) {
    return React.isValidElement(children)
        ? children
        : isSearchResultFunction(children)
            ? children({ result })
            : null;
}
/**
 * For each of the search results found it repeats the children passed in.
 *
 * - If you pass a render prop as a child it will be called with each search result.
 * - Else if you pass in React Elements they will be repeated for each search result.
 */
export const LoopResults = ({ children }) => {
    const { results } = useSearchResults();
    if (!results)
        return null;
    return (React.createElement(React.Fragment, null, results.map((result, index) => {
        const withIndex = { ...result, index };
        return (React.createElement(SearchResultContext.Provider, { key: result.id, value: withIndex }, returnSearchResultChildren(children, withIndex)));
    })));
};
LoopResults.displayName = 'SearchResults.LoopResults';
// type IsEmptyProps = {};
/**
 * Render content if there no search results found.
 */
export const IsEmpty = ({ children }) => {
    const count = useSelector((state) => state.search.totalCount);
    const searchedTerm = useSelector((state) => state.search.searchedTerm);
    if (!searchedTerm)
        return null;
    if (count != null && count === 0)
        return React.createElement(React.Fragment, null, children);
    return null;
};
IsEmpty.displayName = 'SearchResults.IsEmpty';
// type IsNotEmptyProps = {};
/**
 * Render content if there are search results.
 */
export const IsNotEmpty = ({ children }) => {
    const count = useSelector((state) => state.search.totalCount);
    if (count != null && count > 0)
        return React.createElement(React.Fragment, null, children);
    return null;
};
IsNotEmpty.displayName = 'SearchResults.IsNotEmpty';
/**
 * Button to fetch more results.
 *
 * Utilises [[useSearchResults]] under the hood.
 *
 * - If no more results, returns `null`.
 * - Button disables while fetching.
 * - Optional `loadingChildren` will be rendered if provided and if data fetch is under way.
 */
export const ShowMoreButton = ({ loadingChildren, children, ...props }) => {
    const { isFetching, loadMore, hasMore } = useSearchResults();
    if (!hasMore)
        return null;
    return (React.createElement("button", { disabled: isFetching || !hasMore, onClick: loadMore, ...props }, isFetching && loadingChildren ? loadingChildren : children));
};
ShowMoreButton.displayName = 'SearchResults.ShowMoreButton';
//# sourceMappingURL=searchResults.js.map